import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { inferenceHttp } from '@/lib/api/http-client';
import { personasHttp } from '@/lib/api/personas-client';
import { AudienceService, PersonaRequestDto } from '@inference.engine/api-client/web';
import { PersonasService } from '@inference.engine/persona-api-client/web';
import { useMutation } from '@tanstack/react-query';
import { track } from '@/shared/analytics';

export type CreatePersonaDto = PersonaRequestDto;

interface Params {
  payload: CreatePersonaDto;
}

/**
 * Picks the creation endpoint on the same flag the agents fork on. Both are expected to answer
 * with `{ personaId, status }` and leave the same processing stub, so the polling that follows is
 * unchanged either way. Nothing asserts that the two services agree: each has its own tests, and
 * the shapes meet only here, at runtime.
 *
 * Neither branch passes a workspace header: `configureApiClient` installs
 * `workspaceIdRequestInterceptor` on both clients, so leaving it alone is what keeps the two paths
 * behaving identically when the workspace store has not settled.
 */
export function useCreatePersona() {
  const onRelay = useBooleanFlagValue('relay_runtime', false);

  return useMutation({
    mutationFn: async (params: Params) => {
      if (onRelay) {
        const response = await PersonasService.generate({
          client: personasHttp,
          throwOnError: true,
          body: params.payload,
        });

        return response.data;
      }

      const response = await AudienceService.createAudiencePersona({
        client: inferenceHttp,
        throwOnError: true,
        body: params.payload,
      });

      return response.data;
    },
    onSuccess: (data) => {
      if (data.personaId) {
        // The runtime travels with the event so a persona created during the ramp can be
        // attributed to the path that made it.
        track('persona_created', {
          persona_id: data.personaId,
          // eslint-disable-next-line i18next/no-literal-string -- analytics dimension, not copy
          runtime: onRelay ? 'relay' : 'legacy',
        });
      }
    },
  });
}
